export interface InlineDiffToken {
  text: string;
  kind: "common" | "removed" | "added";
}

const CONTEXT_WORDS_PER_SIDE = 3;

// `white-space: pre-wrap` never wraps at the non-breaking spaces WYSIWYG and pasted text carry.
export const toBreakableSpaces = (text: string): string => text.replace(/\u00a0/g, " ");

const tokenize = (text: string): string[] => text.match(/[A-Za-z0-9']+|[^\sA-Za-z0-9']|\s+/g) ?? [];
const isWord = (token: string): boolean => /\w/.test(token);

const diffMerged = (oldTokens: string[], newTokens: string[]): InlineDiffToken[] => {
  const n = oldTokens.length;
  const m = newTokens.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = oldTokens[i] === newTokens[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const merged: InlineDiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldTokens[i] === newTokens[j]) {
      merged.push({ text: oldTokens[i], kind: "common" });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      merged.push({ text: oldTokens[i], kind: "removed" });
      i++;
    } else {
      merged.push({ text: newTokens[j], kind: "added" });
      j++;
    }
  }
  while (i < n) {
    merged.push({ text: oldTokens[i], kind: "removed" });
    i++;
  }
  while (j < m) {
    merged.push({ text: newTokens[j], kind: "added" });
    j++;
  }
  return merged;
};

const trimToMargin = (tokens: InlineDiffToken[], marginWords: number): InlineDiffToken[] => {
  const firstChanged = tokens.findIndex((t) => t.kind !== "common");
  if (firstChanged === -1) return tokens;
  const lastChanged = tokens.length - 1 - [...tokens].reverse().findIndex((t) => t.kind !== "common");

  let start = firstChanged;
  let wordsSeen = 0;
  while (start > 0 && wordsSeen < marginWords) {
    start--;
    if (isWord(tokens[start].text)) wordsSeen++;
  }

  let end = lastChanged;
  wordsSeen = 0;
  while (end < tokens.length - 1 && wordsSeen < marginWords) {
    end++;
    if (isWord(tokens[end].text)) wordsSeen++;
  }

  const trimmedStart = start > 0;
  const trimmedEnd = end < tokens.length - 1;
  const windowed = tokens.slice(start, end + 1);
  return [
    ...(trimmedStart ? [{ text: "…", kind: "common" as const }] : []),
    ...windowed,
    ...(trimmedEnd ? [{ text: "…", kind: "common" as const }] : []),
  ];
};

export const buildInlineWordDiff = (oldText: string, newText: string): InlineDiffToken[] =>
  trimToMargin(
    diffMerged(tokenize(toBreakableSpaces(oldText)), tokenize(toBreakableSpaces(newText))),
    CONTEXT_WORDS_PER_SIDE,
  );
